import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { listLeads, updateLeadStatus, deleteLead } from "../services/telusgyLeadsService";

const STATUS_OPTIONS = ["nuevo", "contactado", "descartado", "cerrado"];

const STATUS_COLOR = {
  nuevo: "info",
  contactado: "warning",
  descartado: "default",
  cerrado: "success",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleString("sv-SE", { timeZone: "America/La_Paz" });
};

const getError = (error) =>
  error?.response?.data?.error || error?.message || "No se pudo completar la operacion.";

export default function TelusgyLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listLeads();
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (id, status) => {
    setLeads((current) => current.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await updateLeadStatus(id, status);
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
      load();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este lead? Esta acción no se puede deshacer.")) return;
    try {
      await deleteLead(id);
      setToast({ open: true, severity: "success", message: "Lead eliminado." });
      load();
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Leads Telusgy"
        subtitle="Solicitudes capturadas desde el formulario de telusgy.com.bo"
        backTo="/dashboard"
        actions={(
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell align="right">Pago mensual (Bs)</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && !leads.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No hay leads registrados todavía.
                    </TableCell>
                  </TableRow>
                )}
                {leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>{formatDate(lead.createdAt)}</TableCell>
                    <TableCell>{lead.fullName}</TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell>{lead.phone || "-"}</TableCell>
                    <TableCell>{lead.branch}</TableCell>
                    <TableCell align="right">{Number(lead.monthlyBill).toFixed(2)}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        sx={{ minWidth: 130 }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            <Chip
                              size="small"
                              label={opt}
                              color={STATUS_COLOR[opt] || "default"}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(lead.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {!loading && !!leads.length && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {leads.length} lead(s) en total.
          </Typography>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
